import React, { useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubble } from "../styled/App.styled";
import { formatStockChartConfig } from "../utils/Helpers";
import AreaChart from "../components/AreaChart";
import { Spinner } from "reactstrap";
import { useVsiAppContext } from "../stores/VsiApp";

export default function BackupStatus() {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("BACKUP");

  const route = useMemo(
    () => `/api/v6/report/backup_protection_status_proc?client_id=${clientId}&period_id=${periodId}`,
    [clientId, periodId]
  );

  const [data, { isLoading }] = useDataWithRoute(route);

  const { series, chartConfig } = useMemo(() => {
    const rawData = data?.recordsets?.[1] || [];
    const sorted = [...rawData].sort(
      (a, b) => new Date(a.collection_date) - new Date(b.collection_date)
    );

    // Convert raw statuses to daily points
    const groupedByDate = new Map();

    for (const datum of sorted) {
      const dateStr = datum.collection_date; // normalize date only

      if (!groupedByDate.has(dateStr)) {
        groupedByDate.set(dateStr, { collection_date: dateStr, success: 0, warning: 0, failed: 0 });
      }

      const daily = groupedByDate.get(dateStr);

      switch (datum.status) {
        case "kSuccess":
          daily.success += 1;
          break;
        case "kWarning":
          daily.warning += 1;
          break;
        case "kFailed":
          daily.failed += 1;
          break;
        default:
          break;
      }
    }

    // Now build cumulative sums
    const reData = [];
    let last = { success: 0, warning: 0, failed: 0 };

    for (const entry of groupedByDate.values()) {
      const cumulative = {
        collection_date: entry.collection_date,
        success: last.success + entry.success,
        warning: last.warning + entry.warning,
        failed: last.failed + entry.failed,
      };
      reData.push(cumulative);
      last = cumulative;
    }

    const chartInfo = formatStockChartConfig(reData, [
      { name: "Success", field: "success" },
      { name: "Warning", field: "warning" },
      { name: "Failed", field: "failed" },
    ]);

    return {
      series: chartInfo.series,
      chartConfig: {
        colors: ["#8ed600", "#ff8d02", "#E10000"],
      },
    };
  }, [data]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <AreaChart
        series={series}
        stacking="percent"
        navDisabled={true}
        labelFormat="{value}%"
        config={chartConfig}
        height={380}
      />
    </VsiBubble>
  );
}
